// Client-side checks and frame capture for MP4 uploads. Mirrors the iOS capture
// pipeline: a JPEG frame grab (quality 0.86) is uploaded as the separate
// "thumbnail" role alongside the video.

// Server cap (UploadService::MAX_VIDEO_BYTES). The duration matches the iOS
// capture limit — the server never checks it, so the CMS must. The half-second
// over the advertised limit is encoder slack, same as the iOS upload preflight.
export const VIDEO_MIME_TYPE = 'video/mp4'
export const MAX_VIDEO_BYTES = 100 * 1024 * 1024
export const MAX_VIDEO_DURATION_LABEL_SECONDS = 30
export const MAX_VIDEO_DURATION_SECONDS = MAX_VIDEO_DURATION_LABEL_SECONDS + 0.5

export const THUMBNAIL_JPEG_QUALITY = 0.86
// Seeking to the exact end of a clip lands on no frame in some browsers.
export const THUMBNAIL_END_MARGIN_SECONDS = 0.05

export class VideoFileError extends Error {
  constructor(title, message) {
    super(message)
    this.name = 'VideoFileError'
    this.title = title
  }
}

// A source is a local File, or a URL (an already-uploaded clip on the media
// CDN). URL sources load with crossOrigin="anonymous" so their frames can be
// read back out of a canvas, which needs the CDN to send Access-Control-Allow-Origin.
export const isRemoteSource = (source) => typeof source === 'string'

export const videoLoadErrorMessage = (source) =>
  isRemoteSource(source)
    ? 'Could not load this video. The video CDN must allow cross-origin (CORS) reads for the thumbnail picker.'
    : 'Could not read this file as a video.'

const withVideoElement = (source, run) =>
  new Promise((resolve, reject) => {
    const remote = isRemoteSource(source)
    const src = remote ? source : URL.createObjectURL(source)
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.muted = true
    if (remote) video.crossOrigin = 'anonymous'

    const cleanup = () => {
      if (!remote) URL.revokeObjectURL(src)
      video.removeAttribute('src')
      video.load()
    }
    const finish = (result) => {
      cleanup()
      resolve(result)
    }
    const fail = (error) => {
      cleanup()
      reject(error)
    }

    video.onerror = () => fail(new Error(videoLoadErrorMessage(source)))
    run(video, finish, fail)
    video.src = src
  })

export const loadVideoMetadata = (source) =>
  withVideoElement(source, (video, finish, fail) => {
    video.onloadedmetadata = () => {
      if (!isFinite(video.duration) || video.videoWidth === 0) {
        fail(new Error(videoLoadErrorMessage(source)))
        return
      }
      finish({ durationSeconds: video.duration, width: video.videoWidth, height: video.videoHeight })
    }
  })

export const clampThumbnailTime = (atSeconds, durationSeconds) =>
  Math.max(0, Math.min(atSeconds, durationSeconds - THUMBNAIL_END_MARGIN_SECONDS))

// Draws the element's current frame at intrinsic dimensions and encodes it as
// the JPEG the "thumbnail" role expects. A remote video served without CORS
// headers taints the canvas, which surfaces as a synchronous SecurityError from
// toBlob — reported as a rejection so callers see one failure path.
export const captureVideoFrame = (video) =>
  new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const context = canvas.getContext('2d')
    if (!context || canvas.width === 0 || canvas.height === 0) {
      reject(new Error('Could not generate a video thumbnail.'))
      return
    }
    try {
      context.drawImage(video, 0, 0, canvas.width, canvas.height)
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Could not generate a video thumbnail.'))),
        'image/jpeg',
        THUMBNAIL_JPEG_QUALITY
      )
    } catch {
      reject(new Error('Cannot read pixels from this video: it was served without CORS headers.'))
    }
  })

// Grabs the frame at `atSeconds` (default ~0.1s, dodging black first frames).
export const generateVideoThumbnail = (source, atSeconds = 0.1) =>
  withVideoElement(source, (video, finish, fail) => {
    video.onloadedmetadata = () => {
      video.currentTime = clampThumbnailTime(atSeconds, video.duration)
    }
    video.onseeked = () => {
      captureVideoFrame(video).then(finish, fail)
    }
  })

// Type, size and duration checks, then the poster frame. Rejects with a
// VideoFileError carrying the title/message pair shown to the admin.
export const validateVideoFile = async (file) => {
  if (file.type !== VIDEO_MIME_TYPE) {
    throw new VideoFileError('Invalid video', 'Videos must be MP4 files.')
  }
  if (file.size > MAX_VIDEO_BYTES) {
    throw new VideoFileError('Video too large', 'Videos must be 100 MB or smaller.')
  }

  try {
    const metadata = await loadVideoMetadata(file)
    if (metadata.durationSeconds > MAX_VIDEO_DURATION_SECONDS) {
      throw new VideoFileError(
        'Video too long',
        `Videos must be ${MAX_VIDEO_DURATION_LABEL_SECONDS} seconds or shorter, matching what the app can capture.`
      )
    }
    return { metadata, thumbnail: await generateVideoThumbnail(file) }
  } catch (error) {
    if (error instanceof VideoFileError) throw error
    throw new VideoFileError('Invalid video', error.message)
  }
}
